import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { companyAPI } from '../../api/axios';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';
import {
  FileText, Search, Loader2, CheckCircle, XCircle,
  Clock, Trash2, ArrowLeft, LogOut, MapPin, Briefcase, User
} from 'lucide-react';

const statusConfig = {
  PENDING:   { label: 'Pending',   color: 'bg-yellow-100 text-yellow-700', icon: Clock },
  APPROVED:  { label: 'Approved',  color: 'bg-green-100  text-green-700',  icon: CheckCircle },
  REJECTED:  { label: 'Rejected',  color: 'bg-red-100    text-red-700',    icon: XCircle },
  WITHDRAWN: { label: 'Withdrawn', color: 'bg-gray-100   text-gray-500',   icon: XCircle },
};

export default function MyHireRequests() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [withdrawLoading, setWithdrawLoading] = useState({});

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const res = await companyAPI.getMyHireRequests();
      setRequests(res.data.data || []);
    } catch { toast.error('Failed to load hire requests'); }
    finally { setLoading(false); }
  };

  const handleWithdraw = async (id) => {
    if (!window.confirm('Withdraw this hire request?')) return;
    setWithdrawLoading((p) => ({ ...p, [id]: true }));
    try {
      await companyAPI.withdrawHireRequest(id);
      setRequests((p) => p.map((r) => r.id === id ? { ...r, status: 'WITHDRAWN' } : r));
      toast.success('Hire request withdrawn');
    } catch (e) { toast.error(e.response?.data?.message || 'Failed to withdraw'); }
    finally { setWithdrawLoading((p) => ({ ...p, [id]: false })); }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <Loader2 className="h-10 w-10 animate-spin text-teal-600" />
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navbar */}
      <nav className="bg-white border-b border-gray-100 shadow-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/company/dashboard')}
              className="text-gray-400 hover:text-gray-600 transition p-1 rounded-lg hover:bg-gray-100">
              <ArrowLeft className="h-5 w-5" />
            </button>
            <img src="/logo-horizontal.png" alt="ShramSena" className="h-7 w-auto" />
            <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-lg text-xs font-bold uppercase">Company</span>
          </div>
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/company/search')}
              className="flex items-center gap-1.5 text-sm font-semibold text-teal-600 hover:text-teal-800">
              <Search className="h-4 w-4" /> Search Staff
            </button>
            <button onClick={() => { logout(); navigate('/company/login'); }}
              className="flex items-center gap-1.5 text-sm font-medium text-red-500 hover:text-red-700">
              <LogOut className="h-4 w-4" /> Logout
            </button>
          </div>
        </div>
      </nav>

      <div className="max-w-5xl mx-auto px-6 py-8">

        <div className="flex items-center gap-3 mb-6">
          <FileText className="h-6 w-6 text-teal-600" />
          <h2 className="text-xl font-bold text-slate-800">My Hire Requests</h2>
          <span className="px-2.5 py-0.5 bg-teal-100 text-teal-700 rounded-full text-sm font-semibold">
            {requests.length}
          </span>
        </div>

        {requests.some((r) => r.status === 'APPROVED') && (
          <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-5 text-sm text-green-800">
            ✅ Approved requests — ShramSena admin will share staff contact details with you directly.
          </div>
        )}

        {requests.length === 0 ? (
          <div className="text-center py-24 bg-white rounded-2xl border border-gray-100 shadow-sm">
            <FileText className="h-14 w-14 text-gray-200 mx-auto mb-4" />
            <h3 className="text-lg font-bold text-gray-500">No hire requests yet</h3>
            <p className="text-sm text-gray-400 mt-1">Search for staff and send a hire request</p>
            <button onClick={() => navigate('/company/search')}
              className="mt-5 px-5 py-2.5 bg-teal-600 text-white rounded-xl text-sm font-bold hover:bg-teal-700 transition shadow-lg shadow-teal-500/20">
              Search Staff
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {requests.map((req) => {
              const cfg = statusConfig[req.status] || statusConfig.PENDING;
              const StatusIcon = cfg.icon;
              return (
                <div key={req.id}
                  className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
                  <div className="flex items-start justify-between gap-4">

                    {/* Staff info */}
                    <div className="flex items-start gap-4 flex-1">
                      <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-xl
                        flex items-center justify-center text-white font-black text-base shadow-md shadow-teal-500/20 shrink-0">
                        {req.staffFirstName?.charAt(0)}{req.staffLastName?.charAt(0)}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <h3 className="font-bold text-slate-800">
                            {req.staffFirstName} {req.staffLastName}
                          </h3>
                          <span className={`flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold ${cfg.color}`}>
                            <StatusIcon className="h-3 w-3" />
                            {cfg.label}
                          </span>
                        </div>

                        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500 mt-1">
                          {req.staffTypeName && (
                            <span className="flex items-center gap-1">
                              <Briefcase className="h-3 w-3" /> {req.staffTypeName}
                            </span>
                          )}
                          {req.staffQualification && (
                            <span className="flex items-center gap-1">
                              <User className="h-3 w-3" />
                              {req.staffQualification}
                              {req.staffExperienceYears != null ? ` · ${req.staffExperienceYears} yrs exp` : ''}
                            </span>
                          )}
                          {req.staffCity && (
                            <span className="flex items-center gap-1">
                              <MapPin className="h-3 w-3" />
                              {req.staffCity}{req.staffState ? `, ${req.staffState}` : ''}
                            </span>
                          )}
                        </div>

                        {req.message && (
                          <p className="text-sm text-gray-500 mt-2 italic bg-gray-50 rounded-lg px-3 py-1.5">
                            "{req.message}"
                          </p>
                        )}
                        {req.adminNote && (
                          <p className="text-xs text-red-500 mt-1.5 font-medium">
                            Admin note: {req.adminNote}
                          </p>
                        )}
                        <p className="text-xs text-gray-400 mt-2">
                          Submitted {new Date(req.createdAt).toLocaleDateString('en-IN', {
                            day: 'numeric', month: 'short', year: 'numeric'
                          })}
                        </p>
                      </div>
                    </div>

                    {/* Withdraw */}
                    {req.status === 'PENDING' && (
                      <button onClick={() => handleWithdraw(req.id)}
                        disabled={withdrawLoading[req.id]}
                        className="flex items-center gap-1.5 text-red-500 hover:text-red-700
                          text-sm font-semibold disabled:opacity-50 transition shrink-0">
                        {withdrawLoading[req.id]
                          ? <Loader2 className="h-4 w-4 animate-spin" />
                          : <><Trash2 className="h-4 w-4" /> Withdraw</>}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}